/**
 * Onglet du navigateur où la page est chargée, gardé dans son `sessionStorage` (propre à chaque
 * onglet, conservé par un rechargement ou une navigation). Le lecteur regroupe ainsi les windows
 * (une par chargement de page) par onglet, sans le déduire des horaires.
 *
 * Un onglet dupliqué (ou ouvert par `window.open` sans `noopener`) reçoit une copie du
 * `sessionStorage` de l'autre : l'identifiant y est marqué « ouvert » tant que sa page l'est
 * (`pagehide` le libère, `pageshow` le reprend au retour du cache de navigation). Une page qui
 * le trouve ouvert est donc la copie d'un onglet encore ouvert : elle en prend un nouveau.
 */

import { migrateLegacyKey } from './legacy-storage.js'
import { uuidv7 } from './uuid.js'

type StoredTab = { id: string; open: boolean }

/** UUID tel qu'accepté par l'ingestion (`tabId`) : une valeur altérée est remplacée. */
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i

export type BrowserTab = {
  /**
   * Identifiant de l'onglet, réservé par cette page au premier appel (rien n'est écrit avant) ;
   * `undefined` si le `sessionStorage` est inaccessible : le lecteur déduira l'onglet.
   */
  id(): string | undefined
}

export function browserTab(publicKey: string): BrowserTab {
  const key = `belvdr:tab:${publicKey}`
  // Lu dès maintenant : la page garde le `sessionStorage` de son onglet. Il peut être
  // inaccessible (iframe isolée, cookies bloqués).
  let storage: Storage | null
  try {
    storage = sessionStorage
  } catch {
    storage = null
  }
  let id: string | undefined
  let claimed = false

  function save(tab: StoredTab) {
    try {
      storage?.setItem(key, JSON.stringify(tab))
      return true
    } catch {
      return false
    }
  }

  return {
    id() {
      if (claimed || !storage) return id
      claimed = true
      // Onglet d'avant le renommage (`belvedere:tab:<clé publique>`) : repris, puis effacé.
      migrateLegacyKey(storage, `belvedere:tab:${publicKey}`, key)
      const stored = read(storage, key)
      const candidate = stored && !stored.open ? stored.id : uuidv7()
      if (!save({ id: candidate, open: true })) return undefined
      id = candidate
      window.addEventListener('pagehide', () => save({ id: candidate, open: false }))
      window.addEventListener('pageshow', (event) => {
        if (event.persisted) save({ id: candidate, open: true })
      })
      return id
    },
  }
}

function read(storage: Storage, key: string): StoredTab | null {
  try {
    const raw = storage.getItem(key)
    const tab = raw === null ? null : (JSON.parse(raw) as Partial<StoredTab> | null)
    return typeof tab?.id === 'string' && UUID.test(tab.id) && typeof tab.open === 'boolean'
      ? { id: tab.id, open: tab.open }
      : null
  } catch {
    return null
  }
}
